/**
 * meters/normalizer.ts — Header-aware raw-row builder
 * =====================================================
 * Turns one CSV data row into a canonical `RawMeterRow` using a MeterModel's
 * field map. Matching is by HEADER NAME, not by fixed column index, so the
 * pipeline is robust to column reordering or inserted columns in the sheet.
 *
 * This replaces the old hard-coded index reader in sheetsClient.ts, which
 * assumed every sheet used the LK4410 column order and therefore produced
 * garbage for the LK4405 layout (e.g. it read VA_Total out of the PF column).
 */

import type { RawMeterRow } from '@/types';
import type { MeterModel } from './types';
import { emptyRawRow, normalizeHeader, sanitizeRawRow, toNum, toStr } from './helpers';

/** Field names that should be read as strings rather than numbers. */
const STRING_FIELDS = new Set<keyof RawMeterRow>(['mdOccurrenceTime']);

/**
 * Build a normalized header → column-index map from a sheet's header row.
 * Later duplicate headers do not overwrite earlier ones (first wins).
 */
export function buildHeaderIndex(headerRow: string[]): Map<string, number> {
  const map = new Map<string, number>();
  headerRow.forEach((h, i) => {
    const key = normalizeHeader(h);
    if (key && !map.has(key)) map.set(key, i);
  });
  return map;
}

/** Resolve the column index for a header spec (string or alias list). */
function resolveIndex(
  spec: string | string[],
  headerIndex: Map<string, number>,
): number | undefined {
  const candidates = Array.isArray(spec) ? spec : [spec];
  for (const c of candidates) {
    const idx = headerIndex.get(normalizeHeader(c));
    if (idx !== undefined) return idx;
  }
  return undefined;
}

/**
 * Build a canonical RawMeterRow for one data row.
 *
 * @param model        The meter model whose field map / derivations to apply.
 * @param headerIndex  normalizeHeader → column index (from buildHeaderIndex).
 * @param cols         The data row's cells.
 * @param ts           The already-parsed timestamp for this row.
 */
export function buildRawRowFromHeaders(
  model: MeterModel,
  headerIndex: Map<string, number>,
  cols: string[],
  ts: Date,
): RawMeterRow {
  const row = emptyRawRow(ts);
  const whScale = model.energyWhScale ?? 1;
  const vahScale = model.energyVahScale ?? 1;

  for (const [field, spec] of Object.entries(model.fieldMap) as [keyof RawMeterRow, string | string[]][]) {
    const idx = resolveIndex(spec, headerIndex);
    if (idx === undefined) continue;
    const cell = cols[idx];

    if (STRING_FIELDS.has(field)) {
      (row[field] as unknown as string) = toStr(cell);
      continue;
    }

    let value = toNum(cell);
    if (field === 'whReceived') value *= whScale;
    if (field === 'vahReceived') value *= vahScale;
    (row[field] as unknown as number) = value;
  }

  // Model-specific derivations + alias sync, then repair bad register decodes.
  return sanitizeRawRow(model.finalizeRaw(row));
}
